<template>
    <div class="transfer-option" :data-transfer-option="option.Id">
        <TransferDescription :transfer="option">
            <template #notifications>
                <Notification
                    v-if="messageWarning"
                    variant="warning"
                    icon="InfoCircle"
                    :tier="1">
                    {{ messageWarning }}
                    <ButtonLink :onClick="handleAdjustPickupTime" size="m" :noPadding="true">
                        {{ t({ key: 'shared.change_pickup_time' }) }}
                    </ButtonLink>
                </Notification>

                <Notification
                    v-if="messageInvalid"
                    variant="negative"
                    icon="InfoCircle"
                    :tier="1">
                    {{ messageInvalid }}
                    <ButtonLink :onClick="handleAdjustPickupTime" size="m" :noPadding="true">
                        {{ t({ key: 'shared.change_pickup_time' }) }}
                    </ButtonLink>
                </Notification>
            </template>
        </TransferDescription>

        <div class="transfer-option-footer">
            <div class="transfer-option-price">
                <span class="transfer-option-cost">
                    {{ getMoneyObjectAsString(cost) }}
                </span>

                <Fees :option="option" />
            </div>
            <ButtonPrimary
                :onClick="() => onSelect && onSelect(option)"
                :disabled="!onSelect">
                {{ t({ key: 'shared.choose' }) }}
            </ButtonPrimary>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { TransferOption, TransferOptionItem } from '@/api/Transfer/transfer.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { Money } from '@libs/money/money.interfaces';
    import { cleanTransferImage, mapTransferPrice } from '@/helpers/transfer/transfer';
    import {
        TransferTrip,
        TransferValidateType,
    } from '@/helpers/transfer/transfer.interfaces';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import Fees from './Fees/Fees.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import TransferDescription from '@/components/checkoutPageV3/Sections/Addons/Transfer/Trips/Trip/Shared/TransferDescription/TransferDescription.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';

    export default defineComponent({
        name: 'TransferTripSelectionOption',
        methods: { t, cleanTransferImage, getMoneyObjectAsString },
        components: {
            ButtonLink,
            ButtonPrimary,
            Notification,
            Fees,
            TransferDescription,
        },
        props: {
            option: { type: Object as PropType<TransferOptionItem>, required: true },
            trip: { type: Object as PropType<TransferTrip>, required: true },
            onSelect: { type: Function as PropType<(option: TransferOption) => void> },
            onAdjustPickupTime: { type: Function as PropType<() => void> },
        },
        setup({ option, onAdjustPickupTime }) {
            const cost = computed<Money>(() => mapTransferPrice(option));

            const messageWarning = computed<string | undefined>(() => {
                if (option.Validation?.Type === TransferValidateType.Warning) {
                    return option.Validation?.Message;
                }

                return undefined;
            });

            const messageInvalid = computed<string | undefined>(() => {
                if (option.Validation?.Type === TransferValidateType.Invalid) {
                    return option.Validation?.Message;
                }

                return undefined;
            });

            function handleAdjustPickupTime() {
                onAdjustPickupTime && onAdjustPickupTime();
            }

            return {
                cost,
                handleAdjustPickupTime,
                messageWarning,
                messageInvalid,
                LabelFormat,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Option.scss';
</style>
